import { VerificationAttempt } from './verificationReportSummary';

/**
 * A verification run lives only in the browser that started it: the checks
 * are called from the client and there is no server-side run record. This
 * keeps one record per entity in localStorage so a reload, or a second tab in
 * the same browser, sees the run in progress (and does not start a duplicate
 * set of third-party lookups), then picks up how it ended.
 *
 * It cannot see runs started in another browser or by another user.
 */

/**
 * Longest a run can take. Outlives the run request's own 120s timeout, so a
 * second tab never starts duplicate lookups while the first is still waiting.
 */
export const VERIFICATION_RUN_MAX_MS = 130 * 1000;

export interface StoredVerificationRun {
  startedAt: number;
  finishedAt?: number;
  outcome?: VerificationAttempt | null;
}

const storageKey = (entityId: string): string =>
  `bite.businessVerification.${entityId}`;

export const verificationRunStorageKey = storageKey;

export const readVerificationRun = (
  entityId: string,
): StoredVerificationRun | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(entityId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.startedAt === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

const write = (entityId: string, run: StoredVerificationRun) => {
  try {
    window.localStorage.setItem(storageKey(entityId), JSON.stringify(run));
  } catch {
    // Storage blocked or full: the run still works, it just is not shared.
  }
};

export const isVerificationRunActive = (
  run: StoredVerificationRun | null,
  now = Date.now(),
): boolean =>
  Boolean(
    run && !run.finishedAt && now - run.startedAt < VERIFICATION_RUN_MAX_MS,
  );

export const markVerificationRunStarted = (
  entityId: string,
  startedAt: number,
): void => write(entityId, { startedAt: startedAt });

/** Only the run that holds the record may close it; a newer run keeps its lock. */
export const markVerificationRunFinished = (
  entityId: string,
  startedAt: number,
  outcome: VerificationAttempt | null,
): void => {
  const current = readVerificationRun(entityId);
  if (current && current.startedAt !== startedAt) return;
  write(entityId, {
    startedAt: startedAt,
    finishedAt: Date.now(),
    outcome: outcome,
  });
};
